import {Component, ElementRef, Input, OnDestroy, Renderer2} from '@angular/core';
import {NgbActiveModal, NgbDateStruct, NgbInputDatepicker, NgbModal, NgbTypeahead} from '@ng-bootstrap/ng-bootstrap';
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  AsyncPipe,
  DatePipe,
  KeyValuePipe,
  NgClass,
  NgForOf,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault
} from '@angular/common';
import {Observable, Subscription} from 'rxjs';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ToastService} from "../../../../services/toaster.service";
import {BaseComponent} from "../../../base/base.component";
import {Cinema} from "../../../../models/cinema.model";
import {Version} from "../../../../models/transport.model";
import {SearchFilmService} from "../../../../services/search-film.service";
import {CirculationApiService} from "../../../../services/api/circulation-api.service";
import {Circulation, PartageCopie, Sem} from "../../../../models/circulations.model";
import {RemoveModalComponent} from "../planning-region-remove-reser-modal/remove-modal.component";
import {ADRC, GPCI, LIST_STATUS} from "../../../../consts/status.const";
import {dateUtils} from "../../../../helpers/date.utils";
import {nextWednesday} from 'date-fns';

@Component({
  selector: 'app-gestion-circulation-modal',
  templateUrl: './planning-region-gestion-circulation-modal.component.html',
  styleUrls: ['./planning-region-gestion-circulation-modal.component.scss'],
  standalone: true,
  imports: [FormsModule, NgIf, NgForOf, NgbTypeahead, TranslateModule, NgbInputDatepicker, KeyValuePipe, DatePipe, NgClass, NgSwitch, NgSwitchDefault, NgSwitchCase, ReactiveFormsModule, AsyncPipe],
})
export class PlanningRegionGestionCirculationModalComponent extends BaseComponent implements OnDestroy {
  @Input() title: string = '';

  versions: Version[] = [];
  films: any[] = [];
  selectedFilm: any = null;
  circulations: Circulation[] = [];
  editingCirculation: Circulation | null = null;
  private temporaryCirculation: any;
  circulationForm: FormGroup;
  cinemasMap: { [key: string]: Cinema[] } = {};
  cinemasPdcMap: { [key: string]: Cinema[] } = {};
  cinemas?: Cinema[] = [];

  private subscriptions: Subscription = new Subscription();

  protected readonly Object = Object;
  protected readonly event = event;
  listStatus: any = LIST_STATUS;
  gpci: number = GPCI;

  constructor(
    public activeModal: NgbActiveModal,
    private toastService: ToastService,
    private translate: TranslateService,
    private searchFilmService: SearchFilmService,
    private circulationApiService: CirculationApiService,
    private modalService: NgbModal,
    private fb: FormBuilder,
    private elRef: ElementRef,
    private renderer: Renderer2,
  ) {
    super();
    this.circulationForm = this.fb.group({
      is_adrc: [false],
      copie_distri: [false],
      date_debut: [null],
      etat: [null],
      sem: this.fb.array([
        this.createWeekFormGroup()
      ]),
    });
  }

  scrollToDiv() {
    const element = document.getElementById("editCirculation");
    if (element) {
      element.scrollIntoView({behavior: 'smooth'});
    }
  }

  createWeekFormGroup(sem: Sem | null = null): FormGroup {
    return this.fb.group({
      date_debut: [sem?.date_debut || null],
      id_cinema: [sem?.id_cinema || null],
      id_reser: [sem?.id_reser || null],
      nb_seances: [sem?.nb_seances || null, [Validators.min(0)]],
      nb_seances_max: [sem?.nb_seances_max || null, [Validators.min(0)]],
      partage_de_copie: sem?.partage_de_copie ? this.fb.array(
        sem.partage_de_copie.map((pdc) => this.createPdcGroup(pdc))
      ) : this.fb.array([]),
    });
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  closeModal(): void {
    this.activeModal.close();
  }

  searchFilms = (text$: Observable<string>) => this.searchFilmService.searchFilms(text$, this.cookies);

  formatter = (film: any) => this.searchFilmService.formatter(film);

  onFilmSelect(event: any): void {
    this.selectedFilm = event.item;
    this.editingCirculation = null;
    this.getCirculations();
    if (this.selectedFilm){
      this.showAddCirc();
    }
  }

  getCirculations(){
    this.circulationApiService.getCirculations(this.cookies, this.selectedFilm.id_film).subscribe({
      next: (response) => {
        this.circulations = Object.values(response.circulations);

        for (let circul of this.circulations) {
          circul.sem = Object.values(circul.sem);
        }
      },
      error: () => {
        this.toastService.showDanger('toast.error');
      }
    });
  }

  async editCirculation(circulId: number, newCirc: Circulation | null = null): Promise<void> {
    this.editingCirculation = newCirc? newCirc : this.circulations[circulId];
    await this.patchCirculationForm(!!newCirc);
    this.scrollToDiv();
  }


  private async patchCirculationForm(newCirc: boolean = false): Promise<void> {
    this.circulationForm.patchValue({
      is_adrc: this.editingCirculation?.type === ADRC,
      copie_distri: this.editingCirculation?.copie_distri,
      date_debut: this.parseDateDebut(this.editingCirculation?.date_debut),
      etat: this.editingCirculation?.etat,
      sem: this.editingCirculation?.sem,
    });

    await this.populateSemArray(newCirc);
  }

  private parseDateDebut(dateDebut: any): NgbDateStruct | null {
    return typeof dateDebut === 'string' ?
      dateUtils.parseDateToNgbDateStruct(dateDebut || '') :
      dateDebut;
  }

  getEditCinema(sem: Sem, newCirc: boolean, date: string): Cinema {
    if (newCirc) {
        return this.cinemasMap[date][0];
    }

    return {
      liv_dep: "",
      id_cinema: sem.id_cinema,
      id_reser: sem.id_reser,
      nom_cinema: sem.nom_cinema,
      liv_ville: sem.liv_ville
    };
  }


  private async populateSemArray(newCirc: boolean = false): Promise<void> {
    const semArray = this.circulationForm.get('sem') as FormArray;
    semArray.clear();

    if (this.editingCirculation && this.editingCirculation.sem) {
      for (const sem of this.editingCirculation.sem) {
        const formattedDate = dateUtils.formatDateStruct(dateUtils.toDate(dateUtils.parseDateToNgbDateStruct(sem.date_debut)));

        try {
          await this.getAllCinemas(formattedDate, newCirc? null : this.getEditCinema(sem, newCirc, formattedDate));
          semArray.push(this.fb.group({
            date_debut: dateUtils.parseDateToNgbDateStruct(sem.date_debut || ''),
            id_cinema: this.getEditCinema(sem, newCirc, formattedDate).id_cinema,
            nb_seances: sem.nb_seances,
            nb_seances_max: sem.nb_seances_max,
            id_reser: this.getEditCinema(sem, newCirc, formattedDate).id_reser,
            partage_de_copie: this.fb.array(sem.partage_de_copie?.map(pdc => this.createPdcGroup(pdc,
              dateUtils.parseDateToNgbDateStruct(sem.date_debut))) || [])
          }));
        } catch (error) {
          this.toastService.showDanger('toast.error');
        }
      }
    }
  }


  deleteCirculation(circulId: number, index: number): void {
    this.circulations.splice(index, 1);
    this.circulationApiService.removeCirculation(this.cookies, circulId).subscribe({
      next: () => {
        this.toastService.showSuccess('toast.success');
        this.getCirculations();
        this.cancelEdit();
      },
      error: () => {
        this.toastService.showDanger('toast.error');
      }
    });

  }

  removeCirculation(event: Event, circulId: number, index: number): void {
    event.stopPropagation();
    const modalRef = this.modalService.open(RemoveModalComponent, {
      size: 'lg',
      centered: true,
    });
    modalRef.componentInstance.title = this.translate.instant('removeModal.modalHeader.title');
    modalRef.componentInstance.body = this.translate.instant('removeModal.circulation.body');
    modalRef.componentInstance.confirmButtonText = this.translate.instant('removeModal.confirmButton');
    modalRef.componentInstance.cancelButtonText = this.translate.instant('removeModal.cancelButton');

    modalRef.closed.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteCirculation(circulId, index);
      }
    });
  }

  async addCirculation(): Promise<void> {
    this.temporaryCirculation = this.createNewCirculation();
    await this.editCirculation(this.circulations.length + 1, this.temporaryCirculation);
    this.scrollToDiv();
  }

  private createNewCirculation(): any {
    return {
      id_circul: 0,
      date_debut: this.selectedFilm.date_sortie,
      type: '',
      etat: 6,
      is_adrc: false,
      copie_distri: false,
      sem: [
        {
          date_debut: this.selectedFilm.date_sortie,
          id_cinema: null,
          id_reser: null,
          nb_seances: null,
          nb_seances_max: null,
        }
      ],
    };
  }


  onSubmit(): void {
    this.circulationForm.value.id_film = this.selectedFilm.id_film;
    if (this.circulationForm.valid && this.temporaryCirculation) {
      this.circulationApiService.addCirculation(this.cookies, this.circulationForm.value).subscribe({
        next: () => {
          this.toastService.showSuccess('toast.success');
          this.temporaryCirculation = null;
          this.getCirculations();
          this.editingCirculation = null;
        },
        error: () => {
          this.toastService.showDanger('toast.error');
        }
      });
    }
      else if(this.circulationForm.valid && !this.temporaryCirculation){
      this.circulationForm.value.id_circul = this.editingCirculation?.id_circul;
      this.circulationApiService.setCirculation(this.cookies, this.circulationForm.value).subscribe({
        next: () => {
          this.toastService.showSuccess('toast.success');
          this.getCirculations();
          this.editingCirculation = null;
        },
        error: () => {
          this.toastService.showDanger('toast.error');
        }
      });
    }
  }


  removeWeek(event: Event, index: number): void {
    event.stopPropagation();
    const modalRef = this.modalService.open(RemoveModalComponent, {
      size: 'lg',
      centered: true,
    });
    modalRef.componentInstance.title = this.translate.instant('removeModal.modalHeader.title');
    modalRef.componentInstance.body = this.translate.instant('removeModal.circulationWeek.body');
    modalRef.componentInstance.confirmButtonText = this.translate.instant('removeModal.confirmButton');
    modalRef.componentInstance.cancelButtonText = this.translate.instant('removeModal.cancelButton');

    modalRef.closed.subscribe((confirmed: boolean) => {
      if (confirmed) {
        const semArray = this.circulationForm.get('sem') as FormArray;
        semArray.removeAt(index);
        if (this.editingCirculation && this.editingCirculation.sem) {
          this.editingCirculation.sem.splice(index, 1);
        }
      }
    });
  }


  async addWeek(event: Event): Promise<void> {
    event.stopPropagation();
    const lastSem = this.getLastSem();
    const date_sortie = dateUtils.toDate(dateUtils.parseDateToNgbDateStruct(this.selectedFilm.date_sortie));

    const nextWedDate = lastSem ? this.getNextWednesday(lastSem.date_debut)
      : date_sortie;
    try {
      await this.getAllCinemas(dateUtils.formatDateStruct(nextWedDate));
      if (this.editingCirculation && this.cinemasMap[dateUtils.formatDateStruct(nextWedDate)]) {
        const newSem = this.createNewSem(nextWedDate);
        if (newSem) {
          this.editingCirculation.sem.push(newSem);
          this.addSemToForm(newSem, nextWedDate);
        }
      }
    } catch (error) {
      this.toastService.showDanger('toast.error');
    }
  }

  async onDateChange(event: NgbDateStruct): Promise<void> {
    const formattedDate = dateUtils.formatDateStruct(dateUtils.toDate(event));
    try {
      await this.getAllCinemas(formattedDate);
    } catch (error) {
      this.toastService.showDanger('toast.error');
    }
  }

  addPdc(event: Event, i: number, date: NgbDateStruct | null, ref_reser: number) {
    const control = this.circulationForm.get(`sem.${i}.partage_de_copie`) as FormArray;
    control.push(this.createPdcGroup(null, date, ref_reser));
  }


  createPdcGroup(partageCopie: PartageCopie | null = null, date: NgbDateStruct | null = null, ref_reser: number = 0): FormGroup {

    const formattedDate = date ? dateUtils.formatDateStruct(dateUtils.toDate(date)) : null;
    let cinemaSelected: Cinema = {
      id_cinema: 0,
      nom_cinema: '',
      liv_ville: '',
      liv_dep: '',
      id_reser: 0
    };

    if (formattedDate) {
      const cinemaList = this.cinemasMap[formattedDate].slice(1);
      if (cinemaList) {
        if (!partageCopie){
          this.cinemasPdcMap[ref_reser] = [...cinemaList];
          cinemaSelected =  this.cinemasPdcMap[ref_reser][0]
        }
        else{
          const cinemaPdc = {
            liv_dep: '',
            id_cinema: partageCopie.id_cinema,
            id_reser: partageCopie.id_reser,
            nom_cinema: partageCopie.nom_cinema,
            liv_ville: partageCopie.liv_ville
          };
          this.cinemasPdcMap[partageCopie.id_reser] = [cinemaPdc, ...cinemaList];
          cinemaSelected =  this.cinemasPdcMap[partageCopie.id_reser][0]
        }
      }
    }
    return this.fb.group({
      id_cinema: [null],
      id_reser: [cinemaSelected.id_reser || null],
      nom_cinema: [null],
      liv_ville: [null],
      nb_seances: [partageCopie?.nb_seances || null, [Validators.min(0)]],
      nb_seances_max: [partageCopie?.nb_seances_max ||null, [Validators.min(0)]],
    });
  }

  removePdc(event: Event, i: number, j: number) {
    const control = this.circulationForm.get(`sem.${i}.partage_de_copie`) as FormArray;
    control.removeAt(j);
  }

  private getLastSem() {
    return this.editingCirculation?.sem[this.editingCirculation?.sem.length - 1];
  }

  private getNextWednesday(dateDebut: string): Date {
    const dateStruct = dateUtils.parseDateToNgbDateStruct(dateDebut);
    const startDate = dateUtils.toDate(dateStruct);
    return nextWednesday(startDate);
  }

  private createNewSem(nextWedDate: Date): Sem | null {
    const formattedDate = dateUtils.formatDateStruct(nextWedDate, false);
    if (!this.editingCirculation) {
      return null;
    }

    return {
      date_modif: "",
      id_cinema: 0,
      id_reser: 0,
      liv_ville: '',
      nom_cinema: '',
      id_circul: this.editingCirculation.id_circul,
      nb_sem: 0,
      date_debut: formattedDate,
      nb_seances: null,
      nb_seances_max: null,
      partage_de_copie: []
    };
  }


  private addSemToForm(newSem: Sem, nextWedDate: Date): void {
    const semArray = this.circulationForm.get('sem') as FormArray;
    const formattedDate = dateUtils.parseDateToNgbDateStruct(dateUtils.formatDateStruct(nextWedDate));
    semArray.push(this.fb.group({
      date_debut: formattedDate,
      id_cinema: this.getEditCinema(newSem, true, dateUtils.formatDateStruct(nextWedDate)).id_cinema,
      id_reser: this.getEditCinema(newSem, true, dateUtils.formatDateStruct(nextWedDate)).id_reser,
      nb_seances: null,
      nb_seances_max: null,
      partage_de_copie: this.fb.array([])
    }));
  }


  cancelEdit(): void {
    this.getCirculations();
    this.editingCirculation = null;
  }


  private getAllCinemas(date: string, editedWeek: Cinema | null = null ): Promise<void> {
    const params = {
      id_film: this.selectedFilm.id_film,
      wedn: date,
    };
    return new Promise((resolve, reject) => {
      const subscription = this.circulationApiService.getCinemas(this.cookies, params).subscribe({
        next: (response) => {
          let CinemaResp = response.cinemas || [];
          if (editedWeek) {
            CinemaResp = [editedWeek, ...CinemaResp];
          }
          this.cinemasMap[date] = CinemaResp;
          resolve();
        },
        error: (err) => {
          this.toastService.showDanger('error', err.message);
          reject(err);
        }
      });
      this.subscriptions.add(subscription);
    });
  }


  getCinemasForDate(date: NgbDateStruct | null): Cinema[] {
    const dateformat = date ? dateUtils.formatDateStruct(dateUtils.toDate(date)) : '';
    return this.cinemasMap[dateformat] || [];
  }

  getCinemasPdc(id_reser: string, ref_reser: string): Cinema[] {
    return this.cinemasPdcMap[id_reser] ?? this.cinemasPdcMap[ref_reser] ?? [];
  }

  get semControls() {
    return (this.circulationForm.get('sem') as FormArray).controls;
  }

  pdcControls(i: number) {
    return (this.circulationForm.get(`sem.${i}.partage_de_copie`) as FormArray).controls;
  }

  showAddCirc(show: boolean = true){
    const addCircElement = this.elRef.nativeElement.querySelector('.show-add-circ');
    this.renderer.setStyle(addCircElement, 'display', show ? 'flex' : 'none');
  }


}
